import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Lock, CheckCircle } from '@phosphor-icons/react';
import { authExtrasAPI, formatApiError } from '../services/api';
import { toast } from 'sonner';
import PasscodeInput from '../components/PasscodeInput';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

export default function PasscodeSetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('choose'); // choose | confirm | done
  const [length, setLength] = useState(6);
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasExisting, setHasExisting] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authExtrasAPI.passcodeStatus();
        setHasExisting(Boolean(data.passcode_set));
      } catch (err) {
        console.error('Passcode status check failed:', err);
        setHasExisting(false);
      }
    })();
  }, []);

  const handleFirstComplete = (code) => {
    setFirst(code);
    if (code.length === length) {
      setStep('confirm');
      setSecond('');
    }
  };

  const handleConfirmComplete = async (code) => {
    setSecond(code);
    if (code.length !== length) return;
    if (code !== first) {
      toast.error('Passcodes don\'t match. Try again.');
      setStep('choose');
      setFirst('');
      setSecond('');
      return;
    }
    setLoading(true);
    try {
      await authExtrasAPI.setPasscode(code);
      setStep('done');
    } catch (err) {
      toast.error(formatApiError(err));
      setStep('choose');
      setFirst('');
      setSecond('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      <header className="border-b border-[#222222] py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
          <Link to="/profile" className="text-sm text-[#999999] hover:text-white inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to Profile
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {hasExisting && step !== 'done' ? (
            <div className="bg-[#0d150e] border border-[#22C55E]/30 rounded-2xl p-8 text-center" data-testid="passcode-already-set">
              <Lock className="w-12 h-12 text-[#22C55E] mx-auto mb-4" weight="fill" />
              <h1 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>You already have a passcode</h1>
              <p className="text-[#999999] text-sm mb-6">Want to change it? Go to Profile → Security and pick "Change passcode".</p>
              <Link to="/profile">
                <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 px-6 font-semibold w-full">Back to Profile</Button>
              </Link>
            </div>
          ) : step === 'done' ? (
            <div className="bg-[#0d150e] border border-[#22C55E]/30 rounded-2xl p-8 text-center" data-testid="passcode-done">
              <CheckCircle className="w-14 h-14 text-[#22C55E] mx-auto mb-4" weight="fill" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>Passcode set!</h1>
              <p className="text-[#cccccc] text-sm mb-6">You'll be asked for it after logging in to unlock your account.</p>
              <Button onClick={() => navigate('/dashboard')} className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-11 px-6 font-semibold w-full" data-testid="passcode-finish-btn">
                Go to Dashboard
              </Button>
            </div>
          ) : (
            <div className="bg-[#0d150e] border border-[#22C55E]/20 rounded-2xl p-8" data-testid="passcode-setup-panel">
              <Lock className="w-10 h-10 text-[#22C55E] mb-3" weight="duotone" />
              <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>
                {step === 'choose' ? 'Create your passcode' : 'Confirm your passcode'}
              </h1>
              <p className="text-[#999999] text-sm mb-6">
                {step === 'choose'
                  ? `Pick a ${length}-digit passcode. You'll enter it after logging in, like unlocking a banking app.`
                  : 'Enter the same passcode again to confirm.'}
              </p>

              {step === 'choose' && (
                <div className="flex justify-center gap-2 mb-5 text-xs" data-testid="passcode-length-picker">
                  {[4, 5, 6].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => { setLength(n); setFirst(''); }}
                      className={`px-3 py-1.5 rounded-md font-semibold tracking-wider transition-colors ${
                        length === n
                          ? 'bg-[#22C55E] text-[#111111]'
                          : 'bg-[#111111] text-[#999999] border border-[#333333] hover:text-white'
                      }`}
                      data-testid={`passcode-length-${n}`}
                    >
                      {n} digits
                    </button>
                  ))}
                </div>
              )}

              <PasscodeInput
                length={length}
                value={step === 'choose' ? first : second}
                onChange={step === 'choose' ? handleFirstComplete : handleConfirmComplete}
                disabled={loading}
                autoFocus
              />

              {loading && <p className="text-center text-xs text-[#999999] mt-4">Saving…</p>}

              <p className="text-center text-xs text-[#666666] mt-6">
                Forgot it later? You can always reset your passcode by signing out and signing back in.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
